import React, { useRef, useEffect } from 'react';
import classNames from 'classnames';

const hexToRgb = hex => {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16),
      ]
    : [0, 0, 0];
};

const map = (value, start1, stop1, start2, stop2) => {
  return start2 + (stop2 - start2) * ((value - start1) / (stop1 - start1));
};

const CanvasBackground = ({
  className,
  color = '#ffffff',
  zoom = 1,
  trailOpacity = 0.1,
  trailColor = '#000000',
  trail = true,
}) => {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const animationFrameRef = useRef(null);
  const jRef = useRef(2.3);
  const plusJRef = useRef(0);
  const plusRadntRef = useRef(0);
  const ydivRef = useRef(10000);
  const mouseXRef = useRef(0);
  const mouseYRef = useRef(0);
  const animationSpeedRef = useRef(0.1);
  const horizontalRef = useRef(true);
  const lineWidthRef = useRef(1);
  const pointSizeRef = useRef(1);
  const rotationSpeedRef = useRef(0.05);
  const zoomLevelRef = useRef(zoom);
  const trailRef = useRef(trail);
  const trailOpacityRef = useRef(trailOpacity);
  const trailColorRef = useRef(trailColor);
  const isPausedRef = useRef(false);
  const colorRef = useRef(color);
  const trailRgbRef = useRef(hexToRgb(trailColor));

  useEffect(() => {
    colorRef.current = color;
  }, [color]);

  useEffect(() => {
    zoomLevelRef.current = zoom;
  }, [zoom]);

  useEffect(() => {
    trailOpacityRef.current = trailOpacity;
  }, [trailOpacity]);

  useEffect(() => {
    trailColorRef.current = trailColor;
    trailRgbRef.current = hexToRgb(trailColor);
  }, [trailColor]);

  useEffect(() => {
    trailRef.current = trail;
  }, [trail]);

  useEffect(() => {
    const canvas = canvasRef.current;
    let resizeObserver;

    const handleResize = () => {
      const parent = canvas.parentElement;
      if (parent) {
        const parentRect = parent.getBoundingClientRect();
        canvas.width = parentRect.width;
        canvas.height = parentRect.height;
      } else {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }
    };

    const handleMouseMove = e => {
      mouseXRef.current = e.clientX;
      mouseYRef.current = e.clientY;
    };

    const draw = () => {
      if (isPausedRef.current) return;

      const ctx = ctxRef.current;
      const plusRadnt = plusRadntRef.current;
      const plusJ = plusJRef.current;
      const ydiv = ydivRef.current;
      const mouseX = mouseXRef.current;
      const mouseY = mouseYRef.current;
      const animationSpeed = animationSpeedRef.current;
      const horizontal = horizontalRef.current;
      const lineWidth = lineWidthRef.current;
      const pointSize = pointSizeRef.current;
      const rotationSpeed = rotationSpeedRef.current;
      const zoomLevel = zoomLevelRef.current;
      const width = canvas.width;
      const height = canvas.height;

      // Update parameters
      plusRadntRef.current +=
        (map(mouseX, 0, width, 0.00001, 0.000001) - plusRadnt) * animationSpeed;
      plusJRef.current +=
        (map(mouseY, 0, height, -0.00003, 0.00003) - plusJ) * animationSpeed;
      ydivRef.current +=
        (map(mouseX, 0, width, 1000, 1000000) - ydiv) * animationSpeed;

      // Clear or fade previous frame
      if (trailRef.current) {
        // Fade out existing content
        ctx.globalCompositeOperation = 'destination-out';
        ctx.fillStyle = `rgba(0, 0, 0, ${trailOpacityRef.current})`;
        ctx.fillRect(0, 0, width, height);
        ctx.globalCompositeOperation = 'source-over';
      } else {
        ctx.clearRect(0, 0, width, height);
      }

      // Draw new lines
      ctx.strokeStyle = colorRef.current;
      ctx.lineWidth = lineWidth;

      ctx.save();
      ctx.translate(width / 2, height / 2);
      ctx.scale(zoomLevel, zoomLevel);
      ctx.rotate(rotationSpeed);

      let radnt = 0;
      const jCurrent = jRef.current;
      jRef.current += plusJRef.current;

      const ini = horizontal ? -width / 2 : -height / 2;
      ctx.beginPath();

      for (let stp = ini; stp < 0; stp++) {
        radnt += plusRadntRef.current;
        const x = stp + stp * Math.sin(radnt);
        const jx = jCurrent * x;

        if (Math.abs(jx - Math.PI / 2) > 0.0001) {
          const y = x * x * x * Math.tan(jx);
          const xCanvas = horizontal ? x : y / ydivRef.current;
          const yCanvas = horizontal ? y / ydivRef.current : x;

          ctx.moveTo(xCanvas, yCanvas);
          ctx.lineTo(xCanvas + pointSize, yCanvas);
          ctx.moveTo(-xCanvas, yCanvas);
          ctx.lineTo(-xCanvas + pointSize, yCanvas);
        }
      }

      ctx.stroke();
      ctx.restore();

      animationFrameRef.current = requestAnimationFrame(draw);
    };

    // Initialize canvas with transparency
    ctxRef.current = canvas.getContext('2d', { alpha: true });
    handleResize();
    draw();

    // Resize handling
    const parent = canvas.parentElement;
    if (parent) {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(parent);
    } else {
      window.addEventListener('resize', handleResize);
    }

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={classNames('absolute inset-0', className)}
    />
  );
};

export default CanvasBackground;
